import { useEffect, useState } from 'react'
import { useApiQuery } from 'grabbitbot-legacy-ui'
import { projects } from '../../api/fee'
import type { ProjectLimitDef } from '../../api/types'
import { Notice, PageTitle, QueryState } from '../../components/common'
import { useProject } from '../../layout/projectContext'
import { useMoneyAction } from '../../lib/useMoneyAction'

interface MetaForm {
  stripeAccountId: string
  stripeApiVersion: string
  invoiceLineDescriptionPostfix: string
  forwardHubServiceAccountLogin: string
  atomDmpDomain: string
  canEditBillingEmail: number
  canBillingAccountBeReassigned: number
  subscriptionLimits: ProjectLimitDef[]
}

/** Legacy default when the project has no API version yet. */
const DEFAULT_STRIPE_API_VERSION = '2020-08-27'

/** Payment events the backend notifies about (legacy static reference text
 * on this page; ForwardHub e-mail template names). */
const EVENTS: [string, string][] = [
  ['On transaction (Stripe online payment) cancelation', 'payment_transaction_canceled'],
  ['On new subscription payment', 'payment_subscription_setup'],
  ['On existing subscription extension via online payment', 'payment_subscription_extended'],
  ['On unpaid subscription payment', 'payment_subscription_payed'],
]

/**
 * Port of `ProjectEditComponent` (`projects/:projectId/edit`, "Project
 * Config", admin only) — `PATCH /projects/{id}/metas {metas}`.
 *
 * SECURITY (audit §10 D2, fixed with the backend): the tenant's Stripe
 * SECRET key and the ForwardHub service-account password are WRITE-ONLY.
 * The backend never returns them (and src/api/http.ts strips any
 * secret-named field even if it did); the inputs start empty, and an empty
 * input is simply not sent — the backend keeps the stored value. There is no
 * "show value" for a stored secret any more, only for what is being typed.
 */
export function ProjectEditPage() {
  const { projectId, project, refetch } = useProject()
  const q = useApiQuery(() => projects.view(projectId), [projectId])
  const [form, setForm] = useState<MetaForm | null>(null)
  const [stripeKey, setStripeKey] = useState('')
  const [fhPassword, setFhPassword] = useState('')
  const [reveal, setReveal] = useState({ account: false, key: false, fh: false })
  const actions = useMoneyAction()

  useEffect(() => {
    const m = (q.data ?? project).meta ?? {}
    setForm({
      stripeAccountId: String(m.stripeAccountId ?? ''),
      stripeApiVersion: String(m.stripeApiVersion || DEFAULT_STRIPE_API_VERSION),
      invoiceLineDescriptionPostfix: String(m.invoiceLineDescriptionPostfix ?? ''),
      forwardHubServiceAccountLogin: String(m.forwardHubServiceAccountLogin ?? ''),
      atomDmpDomain: String(m.atomDmpDomain ?? ''),
      canEditBillingEmail: m.canEditBillingEmail ? 1 : 0,
      canBillingAccountBeReassigned: m.canBillingAccountBeReassigned ? 1 : 0,
      subscriptionLimits: Array.isArray(m.subscriptionLimits) ? m.subscriptionLimits.map((l) => ({ ...l })) : [],
    })
  }, [q.data, project])

  const save = async () => {
    if (!form) return
    const metas: Record<string, unknown> = {
      ...form,
      subscriptionLimits: form.subscriptionLimits.filter((l) => l.key.trim()).map((l) => ({ key: l.key.trim(), description: l.description ?? '' })),
    }
    if (stripeKey.trim()) metas.stripeKey = stripeKey.trim()
    if (fhPassword) metas.forwardHubServiceAccountPassword = fhPassword
    // The fingerprint deliberately excludes the secret values themselves.
    const r = await actions.run('save', JSON.stringify(form), () => projects.setMetas(projectId, metas))
    if (r) {
      setStripeKey('')
      setFhPassword('')
      actions.setFeedback('save', { tone: 'success', text: 'Saved.' })
      q.refetch()
      refetch()
    }
  }

  const text = (key: keyof MetaForm, label: string, masked?: 'account') => (
    <label key={key}>
      {label}
      <span className="portal-input-group">
        <input
          type={masked && !reveal[masked] ? 'password' : 'text'}
          autoComplete="off"
          value={String(form?.[key] ?? '')}
          onChange={(e) => form && setForm({ ...form, [key]: e.target.value })}
        />
        {masked && (
          <button type="button" onClick={() => setReveal({ ...reveal, [masked]: !reveal[masked] })}>
            {reveal[masked] ? 'Hide value' : 'Show value'}
          </button>
        )}
      </span>
    </label>
  )

  return (
    <div className="portal-card">
      <PageTitle title={`Project config — ${project.name}`} />
      <QueryState loading={q.loading && !form} error={q.error}>
        {form && (
          <form
            className="portal-form fs-form-wide"
            aria-label="Project config"
            onSubmit={(e) => {
              e.preventDefault()
              void save()
            }}
          >
            {text('stripeAccountId', 'Stripe account ID', 'account')}
            <label>
              Stripe secret key
              <span className="portal-input-group">
                <input
                  type={reveal.key ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Stored — enter a new key to replace it"
                  value={stripeKey}
                  onChange={(e) => setStripeKey(e.target.value)}
                />
                <button type="button" onClick={() => setReveal({ ...reveal, key: !reveal.key })}>
                  {reveal.key ? 'Hide value' : 'Show value'}
                </button>
              </span>
              <span className="portal-hint">Write-only: the stored key is never shown. Leave empty to keep it.</span>
            </label>
            {text('stripeApiVersion', 'Stripe API version')}
            {text('invoiceLineDescriptionPostfix', 'Invoice line description postfix')}
            {text('forwardHubServiceAccountLogin', 'ForwardHub service account login')}
            <label>
              ForwardHub service account password
              <span className="portal-input-group">
                <input
                  type={reveal.fh ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Stored — enter a new password to replace it"
                  value={fhPassword}
                  onChange={(e) => setFhPassword(e.target.value)}
                />
                <button type="button" onClick={() => setReveal({ ...reveal, fh: !reveal.fh })}>
                  {reveal.fh ? 'Hide value' : 'Show value'}
                </button>
              </span>
              <span className="portal-hint">Write-only: leave empty to keep the stored password.</span>
            </label>
            {text('atomDmpDomain', 'AtomDMP domain')}
            <label>
              Can the billing e-mail be edited?
              <select value={form.canEditBillingEmail} onChange={(e) => setForm({ ...form, canEditBillingEmail: Number(e.target.value) })}>
                <option value={0}>No</option>
                <option value={1}>Yes</option>
              </select>
            </label>
            <label>
              Can a billing account be reassigned?
              <select
                value={form.canBillingAccountBeReassigned}
                onChange={(e) => setForm({ ...form, canBillingAccountBeReassigned: Number(e.target.value) })}
              >
                <option value={0}>No</option>
                <option value={1}>Yes</option>
              </select>
            </label>
            <fieldset className="fs-fieldset">
              <legend>Subscription limits</legend>
              {form.subscriptionLimits.map((l, i) => (
                <div key={i} className="portal-input-group">
                  <input
                    aria-label={`Limit ${i + 1} key`}
                    placeholder="key"
                    value={l.key}
                    onChange={(e) => {
                      const next = [...form.subscriptionLimits]
                      next[i] = { ...l, key: e.target.value }
                      setForm({ ...form, subscriptionLimits: next })
                    }}
                  />
                  <input
                    aria-label={`Limit ${i + 1} description`}
                    placeholder="description"
                    value={l.description ?? ''}
                    onChange={(e) => {
                      const next = [...form.subscriptionLimits]
                      next[i] = { ...l, description: e.target.value }
                      setForm({ ...form, subscriptionLimits: next })
                    }}
                  />
                  <button
                    type="button"
                    aria-label={`Remove limit ${i + 1}`}
                    onClick={() => setForm({ ...form, subscriptionLimits: form.subscriptionLimits.filter((_, j) => j !== i) })}
                  >
                    −
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => setForm({ ...form, subscriptionLimits: [...form.subscriptionLimits, { key: '', description: '' }] })}>
                + Add limit
              </button>
            </fieldset>
            <button type="submit" disabled={actions.isBusy('save')}>
              {actions.isBusy('save') ? 'Saving…' : 'Save'}
            </button>
            <Notice feedback={actions.feedbackFor('save')} />
          </form>
        )}
      </QueryState>
      <details className="fs-panel">
        <summary>Payment event notifications (ForwardHub e-mail templates)</summary>
        <ul className="fs-plain-list">
          {EVENTS.map(([label, tpl]) => (
            <li key={tpl}>
              {label}: <code>{tpl}</code>
            </li>
          ))}
        </ul>
      </details>
    </div>
  )
}
